/**
 * Árvore de pastas da Biblioteca de modelos ("Meus templates").
 *
 * Componente de apresentação: renderiza a árvore recursiva e emite callbacks.
 * O estado (seleção, dialogs de criar/renomear/deletar) fica no MyTemplatesTab.
 *
 * Cada nó: chevron (se tem filhos) + ícone pasta + nome + contador de templates
 * + menu de ações (criar subpasta, renomear, mover, deletar) no hover.
 */

import { useState } from 'react';
import {
    ChevronRight, ChevronDown, Folder, FolderOpen, MoreVertical,
    FolderPlus, Pencil, Trash2, FolderInput, Files,
} from 'lucide-react';
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
    DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import type { TemplateFolderNode } from '@/types/template-library';

export interface FolderTreeProps {
    folders: TemplateFolderNode[];
    rootTemplateCount: number;
    selectedFolderId: number | null;
    onSelect: (folderId: number | null) => void;
    onCreateSubfolder: (parentId: number | null) => void;
    onRename: (folder: TemplateFolderNode) => void;
    onMove: (folder: TemplateFolderNode) => void;
    onDelete: (folder: TemplateFolderNode) => void;
}

export default function FolderTree({
    folders, rootTemplateCount, selectedFolderId,
    onSelect, onCreateSubfolder, onRename, onMove, onDelete,
}: FolderTreeProps) {
    return (
        <div className="text-sm">
            {/* Raiz: "Todos os templates" (folderId null) */}
            <button
                type="button"
                onClick={() => onSelect(null)}
                className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left transition-colors ${
                    selectedFolderId === null
                        ? 'bg-violet-100 text-violet-800 font-medium'
                        : 'hover:bg-gray-100 text-gray-700'
                }`}
            >
                <Files className="w-4 h-4 shrink-0 text-gray-500" />
                <span className="flex-1 truncate">Todos (raiz)</span>
                {rootTemplateCount > 0 && (
                    <span className="text-[10px] text-gray-400 tabular-nums">{rootTemplateCount}</span>
                )}
            </button>

            {/* Botão criar pasta na raiz */}
            <button
                type="button"
                onClick={() => onCreateSubfolder(null)}
                className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left text-gray-400 hover:bg-gray-100 hover:text-violet-600 transition-colors"
            >
                <FolderPlus className="w-4 h-4 shrink-0" />
                <span className="text-xs">Nova pasta</span>
            </button>

            <div className="mt-1 space-y-0.5">
                {folders.map((f) => (
                    <FolderNodeRow
                        key={f.id}
                        node={f}
                        depth={0}
                        selectedFolderId={selectedFolderId}
                        onSelect={onSelect}
                        onCreateSubfolder={onCreateSubfolder}
                        onRename={onRename}
                        onMove={onMove}
                        onDelete={onDelete}
                    />
                ))}
            </div>
        </div>
    );
}

function FolderNodeRow({
    node, depth, selectedFolderId,
    onSelect, onCreateSubfolder, onRename, onMove, onDelete,
}: {
    node: TemplateFolderNode;
    depth: number;
    selectedFolderId: number | null;
    onSelect: (id: number | null) => void;
    onCreateSubfolder: (parentId: number | null) => void;
    onRename: (folder: TemplateFolderNode) => void;
    onMove: (folder: TemplateFolderNode) => void;
    onDelete: (folder: TemplateFolderNode) => void;
}) {
    const [expanded, setExpanded] = useState(true);
    const hasChildren = node.children && node.children.length > 0;
    const isSelected = selectedFolderId === node.id;

    return (
        <div>
            <div
                className={`group flex items-center gap-1 rounded-md transition-colors ${
                    isSelected ? 'bg-violet-100' : 'hover:bg-gray-100'
                }`}
                style={{ paddingLeft: `${depth * 14}px` }}
            >
                {/* Chevron expand/collapse */}
                <button
                    type="button"
                    onClick={() => hasChildren && setExpanded((v) => !v)}
                    className={`w-5 h-6 flex items-center justify-center shrink-0 ${
                        hasChildren ? 'text-gray-400 hover:text-gray-700' : 'invisible'
                    }`}
                >
                    {expanded
                        ? <ChevronDown className="w-3.5 h-3.5" />
                        : <ChevronRight className="w-3.5 h-3.5" />}
                </button>

                {/* Nome + ícone (seleciona ao clicar) */}
                <button
                    type="button"
                    onClick={() => onSelect(node.id)}
                    className={`flex-1 flex items-center gap-1.5 py-1.5 text-left min-w-0 ${
                        isSelected ? 'text-violet-800 font-medium' : 'text-gray-700'
                    }`}
                >
                    {isSelected
                        ? <FolderOpen className="w-4 h-4 shrink-0 text-violet-600" />
                        : <Folder className="w-4 h-4 shrink-0 text-gray-400" />}
                    <span className="truncate">{node.name}</span>
                    {node.template_count > 0 && (
                        <span className="text-[10px] text-gray-400 tabular-nums">
                            {node.template_count}
                        </span>
                    )}
                </button>

                {/* Menu de ações */}
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <button
                            type="button"
                            className="w-6 h-6 mr-1 flex items-center justify-center rounded text-gray-400 opacity-0 group-hover:opacity-100 hover:bg-gray-200 hover:text-gray-700 transition-opacity shrink-0"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <MoreVertical className="w-3.5 h-3.5" />
                        </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-44">
                        <DropdownMenuItem onClick={() => onCreateSubfolder(node.id)}>
                            <FolderPlus className="w-3.5 h-3.5 mr-2" /> Nova subpasta
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => onRename(node)}>
                            <Pencil className="w-3.5 h-3.5 mr-2" /> Renomear
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => onMove(node)}>
                            <FolderInput className="w-3.5 h-3.5 mr-2" /> Mover
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                            onClick={() => onDelete(node)}
                            className="text-red-600 focus:text-red-700"
                        >
                            <Trash2 className="w-3.5 h-3.5 mr-2" /> Excluir
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>

            {/* Filhos */}
            {hasChildren && expanded && (
                <div className="space-y-0.5">
                    {node.children.map((c) => (
                        <FolderNodeRow
                            key={c.id}
                            node={c}
                            depth={depth + 1}
                            selectedFolderId={selectedFolderId}
                            onSelect={onSelect}
                            onCreateSubfolder={onCreateSubfolder}
                            onRename={onRename}
                            onMove={onMove}
                            onDelete={onDelete}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

/** Achata a árvore em [{id, name, depth}] pra usar em selects (mover pra...). */
export function flattenFolders(
    folders: TemplateFolderNode[],
    depth = 0,
): Array<{ id: number; name: string; depth: number }> {
    const out: Array<{ id: number; name: string; depth: number }> = [];
    for (const f of folders) {
        out.push({ id: f.id, name: f.name, depth });
        if (f.children?.length) {
            out.push(...flattenFolders(f.children, depth + 1));
        }
    }
    return out;
}
